import { useContext } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { useGetTrialData } from '@atlassian/growth-common';

import { SPAViewContext } from '@confluence/spa-view-context';
import { ConfluenceEdition, AccessStatus, useSessionData } from '@confluence/session-data';
import { getApolloClient, isErrorMarkedAsHandled, markErrorAsHandled } from '@confluence/graphql';

import { useExperimentEnrollment } from './useExperimentEnrollment';
import { useDowngradePaymentLink } from './useDowngradePaymentLink';
import { useDirectToPremiumTrial } from './useDirectToPremiumTrial';

export const usePremiumDirectBuyStandardExperiment = () => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const ELIGIBLE_MODAL_DAYS = [1, 3, 5];

	const apolloClient = getApolloClient();

	const { isEnrolled, isTreatment, cohort } = useExperimentEnrollment();

	// skip eligibility checks if feature flag is off, or cohort is not treatment or control
	const skip = !isEnrolled;

	// edition check
	const { accessStatus, userId, isLoggedIn, edition } = useSessionData();
	const isPremium = edition === ConfluenceEdition.PREMIUM;
	const isUserEligible =
		userId &&
		isLoggedIn &&
		(accessStatus === AccessStatus.LICENSED_ADMIN_ACCESS ||
			accessStatus === AccessStatus.LICENSED_USE_ACCESS);

	// trial status, billing system check, under 100 users check
	const {
		daysLeftInTrial,
		billingSystem,
		trialEndDate,
		error: trialDetailsError,
		loading: trialDetailsLoading,
	} = useGetTrialData({
		client: apolloClient,
		experimentName: 'RecommendPurchasingStandardDuringPremiumEot',
		skip: !isPremium || !isUserEligible || skip,
	});

	const isCCP = billingSystem === 'CCP';
	const isEligibleModalExposureDay =
		daysLeftInTrial && ELIGIBLE_MODAL_DAYS.includes(daysLeftInTrial || 0);

	const isEligibleTrial = isCCP && isEligibleModalExposureDay && !trialDetailsError && trialEndDate;

	// is direct to trial
	const {
		error: directToTrialError,
		loading: directToTrialLoading,
		isDirectToPremiumTrial,
	} = useDirectToPremiumTrial(!isEligibleTrial || skip);

	const isDirectToTrial = isDirectToPremiumTrial && !directToTrialError;

	// no payment details on file, can downgrade and pay
	const {
		userCanPayAndDowngrade,
		hasPaymentDetails,
		changeOfferingAndPayLink,
		error: paymentError,
		loading: paymentLoading,
	} = useDowngradePaymentLink(!isDirectToTrial || skip);

	const canDowngradeAndPay = userCanPayAndDowngrade && !hasPaymentDetails && !paymentError;

	const isValidAudience = Boolean(canDowngradeAndPay);

	// return isSiteAdmin property in analytics
	const { isSiteAdmin } = useContext(SPAViewContext);

	const error = trialDetailsError || directToTrialError || paymentError;
	const loading = trialDetailsLoading || directToTrialLoading || paymentLoading;

	if (error && !isErrorMarkedAsHandled(error)) {
		createAnalyticsEvent({
			type: 'sendOperationalEvent',
			data: {
				action: 'failed',
				actionSubject: 'usePremiumDirectBuyStandardExperiment',
				source: 'RecommendStandardOverPremiumModal',
				attributes: {
					growthFeature: 'RecommendStandardOverPremiumModal',
					trialDetailsError: trialDetailsError?.message,
					directToTrialError: directToTrialError?.message,
					paymentError: paymentError?.message,
				},
			},
		}).fire();
		markErrorAsHandled(error);
	}
	const analyticsAttributes = isValidAudience
		? {
				growthFeature: 'RecommendStandardOverPremiumModal',
				messageId: daysLeftInTrial && `confluence_eot_premium_buy_standard_${daysLeftInTrial}`,
				daysLeftInTrial,
				cohort,
				edition,
				isEligibleTrial: Boolean(isEligibleTrial),
				isDirectToTrial,
				canDowngradeAndPay,
				product: 'confluence',
				recommendedEdition: 'Standard',
				isSiteAdmin,
			}
		: {};

	return {
		error,
		loading,
		cohort,
		hideWhenInPremiumDirectBuyStandardTreatment: isValidAudience && isTreatment,
		changeOfferingAndPayLink,
		trialEndDate,
		daysLeftInTrial,
		isEligible: isValidAudience,
		isEligibleTreatment: isValidAudience && isTreatment,
		analyticsAttributes,
	};
};
